interface SEOHeadProps {
	title?: string;
	description?: string;
	keywords?: string;
	canonicalUrl?: string | null;
	ogType?: "website" | "article";
	ogImage?: string;
	ogImageAlt?: string;
	article?: {
		publishedTime?: string;
		modifiedTime?: string;
		author?: string;
		section?: string;
		tags?: string[];
	};
	noIndex?: boolean;
}

const BASE_URL = "https://portalcib.online";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Portal CIB - Cadastro Imobiliário Brasileiro";

const SEOHead = ({
	title = "CIB - Cadastro Imobiliário Brasileiro",
	description = "Portal de referência sobre a Lei do Cadastro Imobiliário Brasileiro. Informações, notícias, cursos e ferramentas para profissionais e cidadãos. Tudo sobre o 'CPF dos Imóveis'",
	keywords = "CIB, Cadastro Imobiliário Brasileiro, CPF dos Imóveis, registro de imóveis, imóveis, cartório, regularização",
	canonicalUrl = "/",
	ogType = "website",
	ogImage = DEFAULT_IMAGE,
	ogImageAlt,
	article,
	noIndex = false,
}: SEOHeadProps) => {
	const fullTitle = title.includes("CIB") ? title : `${title} | CIB`;
	const fullCanonicalUrl = canonicalUrl
		? canonicalUrl.startsWith("http")
			? canonicalUrl
			: `${BASE_URL}${canonicalUrl}`
		: null;
	const fullOgImage = ogImage.startsWith("http")
		? ogImage
		: `${BASE_URL}${ogImage}`;
	const imageAlt = ogImageAlt ?? fullTitle;
	const robots = noIndex
		? "noindex, follow"
		: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

	return (
		<>
			{/* Primary Meta Tags */}
			<title>{fullTitle}</title>
			<meta name="title" content={fullTitle} />
			<meta name="description" content={description} />
			<meta name="keywords" content={keywords} />
			<meta name="robots" content={robots} />
			<meta name="googlebot" content={robots} />
			{fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
			{fullCanonicalUrl && (
				<>
					<link rel="alternate" hrefLang="pt-BR" href={fullCanonicalUrl} />
					<link rel="alternate" hrefLang="x-default" href={fullCanonicalUrl} />
				</>
			)}

			{/* Open Graph / Facebook */}
			<meta property="og:type" content={ogType} />
			{fullCanonicalUrl && (
				<meta property="og:url" content={fullCanonicalUrl} />
			)}
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:image" content={fullOgImage} />
			<meta property="og:image:alt" content={imageAlt} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:locale" content="pt_BR" />

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image" />
			{fullCanonicalUrl && (
				<meta name="twitter:url" content={fullCanonicalUrl} />
			)}
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={fullOgImage} />
			<meta name="twitter:image:alt" content={imageAlt} />

			{/* Article specific meta tags */}
			{ogType === "article" && article && (
				<>
					{article.publishedTime && (
						<meta
							property="article:published_time"
							content={article.publishedTime}
						/>
					)}
					{article.modifiedTime && (
						<meta
							property="article:modified_time"
							content={article.modifiedTime}
						/>
					)}
					{article.author && (
						<meta property="article:author" content={article.author} />
					)}
					{article.section && (
						<meta property="article:section" content={article.section} />
					)}
					{article.tags &&
						[...new Set(article.tags)].map((tag) => (
							<meta key={tag} property="article:tag" content={tag} />
						))}
				</>
			)}
		</>
	);
};

export default SEOHead;
